import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailFeature } from '../../../models/TrailFeature.model'

import UserService from '../../../services/UserService/UserService'
import { TrailFeatureService } from '../../../services/TrailFeatureService/TrailFeatureService'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import TrailSearch from '../../Trails/TrailSearch/TrailSearch.route'
import Tabs from '../../../components/Tabs/Tabs.component'
import TrailFeatureForm from '../shared/TrailFeatureForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import TrailsWithFeatureForm from '../shared/TrailsWithFeatureForm.component'
import { useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailFeatureSectionIds, trailFeatureSections } from '../shared/TrailFeatureSections'

const TrailFeatureDetail = (): JSX.Element => {
  const trailSearchRef = useRef<{ openImportExport: () => void }>(null)
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFeature])
  const hasTrailPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrail])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isEditingTrailsWithFeature, setIsEditingTrailsWithFeature] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailFeatureService = useMemo(() => new TrailFeatureService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail Feature Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: trailFeature } = useQuery(
    [
      'TrailService.get',
      {
        id: Number(id),
        relations: [],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailFeatureService.get({
        id: Number(id),
        relations: [],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail feature.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
    setIsEditingTrailsWithFeature(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (trailFeature?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailFeatureService
        .delete(trailFeature.id)
        .then(() => {
          SnackbarContext.show(
            `Trail Feature deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the trailFeature list page
          setTimeout(() => {
            navigate(`/trail-features`)
            queryClient.removeQueries([
              'TrailFeatureService.get',
              {
                id: Number(id),
                relations: [],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail Feature failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const tabs = useMemo(() => {
    const tabs = []
    if (trailFeature) {
      if (hasTrailPermissions) {
        tabs.push({
          label: 'Trails',
          content: (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={2} mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<ImportExportIcon />}
                  color="primary"
                  aria-label="show import export"
                  onClick={() => {
                    trailSearchRef.current?.openImportExport()
                  }}
                >
                  Export
                </Button>
                {hasWritePermissions && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setIsEditingTrailsWithFeature(true)
                    }}
                  >
                    Edit Trail Assignments
                  </Button>
                )}
              </Stack>
              <TrailSearch
                nested
                hideHeader
                hideBreadcrumbs
                hideActions
                disableImport
                disablePadding
                defaultParams={{
                  trailFeatureIds: [trailFeature.id],
                }}
                ref={trailSearchRef}
              />
            </Paper>
          ),
        })
      }
    }
    return tabs
  }, [trailFeature, hasTrailPermissions])

  const sectionById = (sectionId: TrailFeatureSectionIds) =>
    trailFeatureSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailFeatureSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {trailFeature && (
        <FormDialog<TrailFeature>
          open={isEditing}
          entity={trailFeature}
          entityLabel="Trail Feature"
          onClose={closeEditor}
        >
          <TrailFeatureForm
            trailFeature={trailFeature}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      {trailFeature && (
        <FormDialog<TrailFeature>
          open={isEditingTrailsWithFeature}
          entity={trailFeature}
          entityLabel="Trails with Feature"
          onClose={() => setIsEditingTrailsWithFeature(false)}
        >
          <TrailsWithFeatureForm
            trailFeature={trailFeature}
            onEdit={handleEdit}
            onCancel={() => setIsEditingTrailsWithFeature(false)}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this trail feature?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trailFeature && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Trail Features',
                          href: '/trail-features',
                        },
                      ]
                      breadcrumbs.push({ label: trailFeature.name })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={trailFeature.name}
                  active={trailFeature.active}
                  subtitle={`${trailFeature.region} · ID ${trailFeature.id}`}
                  meta={[
                    `Updated ${formatDate(trailFeature.updatedAt)}`,
                    `Created ${formatDate(trailFeature.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailFeatureSectionIds.general)}
                      onEdit={editSectionHandler(TrailFeatureSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Name">{trailFeature.name || '-'}</DetailField>
                        <DetailField label="Visibility">{trailFeature.active ? 'Active' : 'Inactive'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailFeatureDetail
